import { MutedAlertCameras, MutedAlertSites, Order } from "./alertsInterfaces";
import { getComparator, setDetectionTypeLower, stableSort } from "./alertsTableFunctions";
import { hideMutedFromView, isAlertMuted } from "./isAlertMuted";
import { applyVlmFilter, VlmFilterType } from "./vlmFilters";

export type NavigableAlertsArgs = {
  alertData: any[] | null | undefined;
  detectionType?: string;
  outcomeFilterType?: string;
  vlmFilterType?: VlmFilterType;
  showVlmFilters?: boolean;
  mutedAlertSites?: MutedAlertSites;
  mutedAlertCameras?: MutedAlertCameras;
  filterType?: string | null;
  order?: string | null;
  orderBy?: string | null;
  /** Prev/next always ran isAlertMuted; the table only hides muted on UNCLAIMED. */
  applyMuteFilter?: boolean;
};

const defaultContext: Omit<NavigableAlertsArgs, "alertData"> = {
  detectionType: "All",
  outcomeFilterType: "All",
  vlmFilterType: "All",
  showVlmFilters: false,
  mutedAlertSites: [],
  mutedAlertCameras: [],
  filterType: undefined,
  order: "asc",
  orderBy: "",
};

let navigableAlertsContext: Omit<NavigableAlertsArgs, "alertData"> = {
  ...defaultContext,
};

export const setNavigableAlertsContext = (
  next: Omit<NavigableAlertsArgs, "alertData">
) => {
  navigableAlertsContext = next;
};

export const getNavigableAlertsContext = () => navigableAlertsContext;

export const readStoredAlertsTableSort = (): {
  order: string | null;
  orderBy: string | null;
} => ({
  order: localStorage.getItem("alertsTableOrder"),
  orderBy: localStorage.getItem("alertsTableOrderBy"),
});

export const isSameAlertRow = (a: any, b: any): boolean => {
  if (!a || !b) return false;
  if (a?.id && b?.id) return a.id === b.id;
  return (
    a.approx_capture_timestamp === b.approx_capture_timestamp &&
    a.window_id === b.window_id
  );
};

export const arrowStateForNavigableIndex = (
  index: number,
  length: number
): { backDisabled: boolean; forwardDisabled: boolean } => {
  if (length <= 1 || index < 0) {
    return { backDisabled: true, forwardDisabled: true };
  }
  return {
    backDisabled: index <= 0,
    forwardDisabled: index >= length - 1,
  };
};

export const filterAlertsBeforeVlm = ({
  alertData,
  detectionType = "All",
  outcomeFilterType = "All",
  mutedAlertSites = [],
  mutedAlertCameras = [],
  filterType,
  applyMuteFilter,
}: NavigableAlertsArgs): any[] => {
  const rows = Array.isArray(alertData) ? alertData : [];
  const shouldMuteFilter =
    applyMuteFilter ?? hideMutedFromView(filterType);
  const visibleRows = shouldMuteFilter
    ? rows.filter(
        (row) => !isAlertMuted(row, mutedAlertSites, mutedAlertCameras)
      )
    : rows;

  if (detectionType === "All" && outcomeFilterType === "All") {
    return visibleRows;
  }
  if (detectionType !== "All" && outcomeFilterType === "All") {
    const label = setDetectionTypeLower(detectionType);
    return visibleRows.filter((row) => row.alert_labels === label);
  }
  if (detectionType === "All" && outcomeFilterType !== "All") {
    return visibleRows.filter(
      (row) => row.detection_type === outcomeFilterType
    );
  }
  const label = setDetectionTypeLower(detectionType);
  return visibleRows.filter(
    (row) =>
      row.alert_labels === label && row.detection_type === outcomeFilterType
  );
};

export const getNavigableAlerts = (args: NavigableAlertsArgs): any[] => {
  const beforeVlm = filterAlertsBeforeVlm(args);
  const rows = args.showVlmFilters
    ? applyVlmFilter(beforeVlm, args.vlmFilterType ?? "All")
    : beforeVlm;

  if (!args.order || !args.orderBy) {
    return rows;
  }
  const order: Order = args.order === "desc" ? "desc" : "asc";
  return stableSort(rows, getComparator(order, args.orderBy));
};
